import React from 'react';
import PropTypes from 'prop-types';
import { Provider, observer, inject } from 'mobx-react';
import get from 'lodash/get';
import { H4 } from '@xo-union/tk-ui-typography';
import Alert from '@xo-union/tk-component-alerts';
import AnalyticsStore from '~/glm/models/analytics-store';
import EventCollection from '~/glm/models/gds/event-collection';
import FullScreenModal from '~/glm/containers/full-screen-modal';
import GuestListStore from '~/glm/models/guest-list-store';
import validators from '~/glm/utils/validators';
import guestCount from '~/glm/utils/guest-count';
import GdsUiState from '~/glm/models/gds/gds-ui-state';
import FullPageModalHeader from '~/glm/components/shared/layout/full-page-modal-header';
import ImportGuestCandidateCollection from '~/glm/models/guest-list/import-guest-candidate-collection';
import GuestImportSpreadsheet from '~/glm/components/shared/guest-import/guest-import-spreadsheet';
import Footer from '~/glm/components/import-spreadsheet/import-spreadsheet-footer';
import {
  ImportSpreadsheetWarningOverlay,
  ImportSpreadsheetSpinnerOverlay,
} from '~/glm/components/import-spreadsheet/import-spreadsheet-overlay';
import RsvpSettingsModal from '~/glm/components/guest-list/modals/rsvp-settings-modal';
import MessageGuestModal from '~/glm/components/guest-list/modals/message-guest-modal';
import ExportGuestListModal from '~/glm/components/export-guest-list/export-modal';
import UseTemplateSection from '~/glm/components/import-spreadsheet/use-template-section';
import styles from './import-spreadsheet-page.scss';
import { withRouter } from 'react-router-dom';

const determineSelectedEvents = (weddingEventId, routeSelectedEvent) => {
  let selectedEvents = new Set();

  if (weddingEventId) selectedEvents.add(weddingEventId);
  if (routeSelectedEvent) selectedEvents.add(routeSelectedEvent);

  return [...selectedEvents];
};

@withRouter
@inject('gdsUiState', 'guestListStore', 'analyticsStore', 'gdsEvents')
@observer
class ImportSpreadsheetPage extends React.Component {
  constructor(props) {
    super(props);
    const { analyticsStore, gdsEvents, router } = props;
    const weddingEventId = get(gdsEvents, ['weddingEvent', 'id']);
    this.collection = new ImportGuestCandidateCollection(analyticsStore);
    this.analyticsStore = analyticsStore;
    this.state = {
      isProcessing: false,
      isSaved: false,
      showWarning: false,
      nextLocation: {},
      showError: false,
      selectedEvents: determineSelectedEvents(weddingEventId, get(router, 'location.query.event')),
    };
  }

  componentDidMount() {
    const { history } = this.props;
    history.block(this.routerWillLeave);
  }

  componentDidUpdate() {
    return this.renderErrorMessage();
  }

  renderErrorMessage = () => {
    const { showError } = this.state;
    if (this.alert && showError) {
      this.setState({ showError: false }, () => {
        this.alert.show({
          type: 'error',
          content: 'Ooops. Something went wrong!',
        });
        this.timeoutId = setTimeout(() => {
          this.alert.dismiss();
        }, 5000);
      });
    }
  };

  componentWillUnmount() {
    clearTimeout(this.timeoutId);
    clearTimeout(this.scrollTimeoutId);
  }

  routerWillLeave = nextLocation => {
    if (!this.state.isSaved && this.collection.count !== 0) {
      this.setState({
        nextLocation,
        showWarning: true,
      });
      return false;
    }
    return undefined;
  };

  onClose = () => {
    const { gdsUiState, router, history } = this.props;
    const referrerRedirectPath = get(router, 'location.query.referrerRedirectPath');

    if (referrerRedirectPath) {
      window.location.assign(referrerRedirectPath);
    } else {
      gdsUiState.setImportSpreadsheetView(false);
      history.push('/our-guest-list');
    }
  };

  onCancel = () => {
    this.setState({
      showWarning: false,
      nextLocation: {},
    });
  };

  onConfirm = () => {
    const { nextLocation } = this.state;
    const { history } = this.props;
    this.collection.clear();
    this.setState(
      {
        showWarning: false,
      },
      () => {
        history.push(nextLocation.pathname);
      }
    );
  };

  handleChange = e => {
    const { value } = e.target;

    this.setState(prevState => {
      const newSelectedEvents = new Set(prevState.selectedEvents);

      if (newSelectedEvents.has(value)) {
        newSelectedEvents.delete(value);
      } else {
        newSelectedEvents.add(value);
      }

      return { ...prevState, selectedEvents: [...newSelectedEvents] };
    });
  };

  handleTrackValidationError = field => {
    const errorType = field === 'names' ? 'missing name' : `${field} validation`;
    return this.analyticsStore.track('Error Message Displayed', {
      source: 'add guest',
      userDecisionArea: 'import from spreadsheet',
      errorType,
      message: validators[field].message,
    });
  };

  handlePaste = ({ row, count, errors }) => {
    const params = {
      action: 'paste guest info',
      pastedRows: count,
      pastedErrorCount: errors,
    };
    this.scrollTimeoutId = setTimeout(() => {
      document.getElementById(`r${row}_c${0}`).focus();
    }, 500);
    return this.analyticsStore.track('Dashboard Interaction', params);
  };

  hasErrors = errorType => {
    return !!this.collection.find(guest => guest[`${errorType}Error`]);
  };

  getErrors = () => {
    return {
      names: !this.collection.validateFields('names'),
      phone: this.hasErrors('phone'),
      email: this.hasErrors('email'),
    };
  };

  trackTemplate = action => {
    this.analyticsStore.track('Dashboard Interaction', {
      viewType: 'import spreadsheet',
      action,
    });
  };

  handleImportClick = () => {
    const { isProcessing } = this.state;
    if (this.spreadsheetHasErrors || isProcessing) return null;
    return this.importGuests();
  };

  trackImportClick = (guestsAdded, guestsTotal) => {
    const params = {
      action: 'import click',
      guestsTotal,
      guestsAdded,
    };
    return this.analyticsStore.track('Dashboard Interaction', params);
  };

  importSuccess = totalGuestsAdded => {
    const { guestListStore, gdsUiState } = this.props;
    const guestOrGuests = totalGuestsAdded === 1 ? 'guest' : 'guests';
    const message = `Got it! We’ve added ${totalGuestsAdded} ${guestOrGuests} to your list.`;
    return guestListStore
      .fetchCollections()
      .then(() => this.trackImportClick(totalGuestsAdded, guestListStore.totalGuestCount))
      .then(() => this.setState({ isSaved: true }, () => gdsUiState.goToAllGuestsView({ message })))
      .catch(() => {
        this.setState({ isProcessing: false, showError: true });
      });
  };

  importGuests = () => {
    const { selectedEvents } = this.state;
    return this.setState({ isProcessing: true }, () =>
      this.collection
        .saveGuests(selectedEvents)
        .then(res => guestCount(res))
        .then(totalGuestsAdded => this.importSuccess(totalGuestsAdded))
        .catch(() => {
          this.setState({ isProcessing: false, showError: true });
        })
    );
  };

  render() {
    const { gdsEvents, gdsUiState } = this.props;
    const { showWarning, isProcessing, selectedEvents } = this.state;

    return (
      <Provider guestCandidates={this.collection}>
        <FullScreenModal>
          <FullPageModalHeader title="Import From Spreadsheet" onClose={this.onClose} />
          <div className={styles.body}>
            <UseTemplateSection />
            <H4 className={styles.copyAndPaste}>2. Copy & Paste</H4>
            <GuestImportSpreadsheet
              handlePaste={this.handlePaste}
              handleTrackValidationError={this.handleTrackValidationError}
              guestCandidates={this.collection}
            />
            <Footer
              events={gdsEvents.nonReceptionEvents}
              isProcessing={isProcessing}
              errors={this.getErrors()}
              guestCandidates={this.collection}
              handleImportClick={this.handleImportClick}
              handleChange={this.handleChange}
              selectedEvents={selectedEvents}
            />
          </div>
          <div className={styles.absoluteAlert}>
            <div className={styles.relative}>
              <Alert
                ref={a => {
                  this.alert = a;
                }}
                position="bottom"
              />
            </div>
          </div>
          <ImportSpreadsheetWarningOverlay
            showWarning={showWarning}
            onCancel={this.onCancel}
            onConfirm={this.onConfirm}
          />
          <ImportSpreadsheetSpinnerOverlay isProcessing={isProcessing} />
          {gdsUiState.rsvpModalView ? <RsvpSettingsModal /> : null}
          {gdsUiState.messageGuestModalView ? <MessageGuestModal /> : null}
          {gdsUiState.exportGuestModalView ? <ExportGuestListModal /> : null}
        </FullScreenModal>
      </Provider>
    );
  }
}

ImportSpreadsheetPage.propTypes = {
  analyticsStore: PropTypes.instanceOf(AnalyticsStore),
  gdsEvents: PropTypes.instanceOf(EventCollection),
  guestListStore: PropTypes.instanceOf(GuestListStore),
  gdsUiState: PropTypes.instanceOf(GdsUiState),
  router: PropTypes.shape({
    setRouteLeaveHook: PropTypes.func,
  }),
  route: PropTypes.shape({
    path: PropTypes.string,
    component: PropTypes.func,
  }),
};

export default ImportSpreadsheetPage;
